/**
 * service-order.ts
 * Task 5.11 — Shared Zod contracts for the Servicios (LOCX) module.
 *
 * LOCX-specific error codes (extend the kernel set for module-level errors):
 */

/**
 * LOCX-specific error code constants.
 * INVALID_STATE_TRANSITION: maps from kernel InvalidTransitionError (INVALID_TRANSITION)
 *   at the HTTP boundary per SL-4-D. The kernel domain error code is INVALID_TRANSITION;
 *   the HTTP response code is INVALID_STATE_TRANSITION (more descriptive to API consumers).
 *   Do NOT rename the kernel constant — only map at the HTTP layer.
 *
 * Spec: SL-4-D / design §5d (HTTP boundary mapping)
 */
export const SERVICIOS_ERROR_CODES = {
  INVALID_STATE_TRANSITION: 'INVALID_STATE_TRANSITION',
  SERVICE_TYPE_INACTIVE: 'SERVICE_TYPE_INACTIVE',
  SERVICE_TYPE_CODE_CONFLICT: 'CATALOG_CODE_CONFLICT',
  /**
   * Asset type code already exists in this (companyId, code) tenant scope.
   * 409 at the HTTP boundary. AT-6.1 / AT-6.2 — distinct from SERVICE_TYPE_CODE_CONFLICT.
   */
  ASSET_TYPE_CODE_CONFLICT: 'ASSET_TYPE_CODE_CONFLICT',
  /**
   * Field key already exists in this (companyId, serviceTypeId) scope.
   * Reuses kernel CATALOG_CODE_CONFLICT pattern — 409 at the HTTP boundary.
   * SL-27-B / design §Backend-Errors
   */
  FIELD_KEY_CONFLICT: 'CATALOG_CODE_CONFLICT',
  /**
   * Field option value is still referenced by at least one service_order_field_value row.
   * Hard-delete is blocked; the operator must reassign or deactivate instead.
   * 409 at the HTTP boundary. SL-27-D / design §Backend-Errors
   */
  FIELD_OPTION_IN_USE: 'FIELD_OPTION_IN_USE',
  /**
   * Caller attempted to add/remove options on a field whose data_type is NOT 'select'.
   * Options only exist on select fields (red line #1). 422 at HTTP boundary.
   * SL-27-E / design §Backend-Errors
   */
  INVALID_DATA_TYPE_FOR_OPTIONS: 'INVALID_DATA_TYPE_FOR_OPTIONS',
  /**
   * Asset attribute def key already exists for this asset_type.
   * 409 at the HTTP boundary. AAD-4.1 / design §PR2 backend.
   */
  ASSET_ATTR_KEY_CONFLICT: 'ASSET_ATTR_KEY_CONFLICT',
  /**
   * Asset attribute option value is still referenced by at least one asset_attribute_value row.
   * Hard-delete of the option is blocked. 409 at the HTTP boundary.
   * AAD-4.1 / design §PR2 backend.
   */
  ASSET_ATTR_OPTION_IN_USE: 'ASSET_ATTR_OPTION_IN_USE',
  /**
   * Caller attempted to add options on an asset_attribute_def whose data_type is NOT 'select'.
   * 422 at the HTTP boundary. AAD-4.1 / design §PR2 backend.
   */
  ASSET_ATTR_NOT_SELECT: 'ASSET_ATTR_NOT_SELECT',
  /**
   * rate_card_item.item_code already exists within the same rate_card.
   * 409 at the HTTP boundary. SL-44.2 / locx-service-progress Slice 1.
   */
  RATE_CARD_ITEM_CODE_CONFLICT: 'RATE_CARD_ITEM_CODE_CONFLICT',
  /**
   * vehicle_document_type.requiresExpiration is true but no expiresAt was given
   * when attaching a document. 422 at the HTTP boundary. Mirrors rrhh's
   * EXPIRATION_REQUIRED (add-nomi-documentacion-epp) — HANDOFF-DOCUMENTOS.md
   * second pass.
   */
  EXPIRATION_REQUIRED: 'EXPIRATION_REQUIRED',
  /**
   * fleet_failure.assignedEmploymentId and externalProviderId were both set —
   * mutually exclusive (interno XOR externo). 422 at the HTTP boundary.
   * add-locx-fleet-failure-reports / design.md §5.
   */
  FLEET_FAILURE_ASSIGNMENT_EXCLUSIVE: 'FLEET_FAILURE_ASSIGNMENT_EXCLUSIVE',
  /**
   * Failure type code already exists in this (companyId, code) tenant scope.
   * 409 at the HTTP boundary. Mirrors ASSET_TYPE_CODE_CONFLICT — distinct
   * discriminated code for the failure_type admin surface (2026-07-21).
   */
  FAILURE_TYPE_CODE_CONFLICT: 'FAILURE_TYPE_CODE_CONFLICT',
  /**
   * fuel_load.receiverEmploymentId and receiverExternalName were both set —
   * mutually exclusive (interno XOR externo). 422 at the HTTP boundary.
   * add-locx-trip-fuel-tracking / design.md §4.
   */
  FUEL_LOAD_RECEIVER_EXCLUSIVE: 'FUEL_LOAD_RECEIVER_EXCLUSIVE',
  /**
   * fuel_load requires EXACTLY ONE of receiverEmploymentId/receiverExternalName —
   * neither was set. 422 at the HTTP boundary.
   * add-locx-trip-fuel-tracking / design.md §4.
   */
  FUEL_LOAD_RECEIVER_REQUIRED: 'FUEL_LOAD_RECEIVER_REQUIRED',
  /**
   * vehicle_document is superseded (already renewed) — read-only history.
   * Returned by PATCH/DELETE on a superseded row, and by the renew endpoint
   * when the source document is itself already superseded. 409 at the HTTP
   * boundary. fleet-qr-compliance PR1 / design.md §2.2.
   */
  DOCUMENT_SUPERSEDED: 'DOCUMENT_SUPERSEDED',
  /**
   * vehicle_document has renewal history (referenced as supersedesId by a
   * later row) — hard delete is blocked to avoid destroying that history.
   * 409 at the HTTP boundary. fleet-qr-compliance PR1 / design.md §2.2.
   */
  DOCUMENT_HAS_HISTORY: 'DOCUMENT_HAS_HISTORY',
  /**
   * A checklist submission is missing an answer for at least one item marked
   * `required: true` on the template. 422 at the HTTP boundary.
   * fleet-qr-compliance PR2 / design.md §3.
   */
  CHECKLIST_MISSING_REQUIRED_ITEMS: 'CHECKLIST_MISSING_REQUIRED_ITEMS',
  /**
   * A checklist submission references an itemId that does not belong to the
   * template being answered. 422 at the HTTP boundary.
   * fleet-qr-compliance PR2 / design.md §3.
   */
  CHECKLIST_ITEM_MISMATCH: 'CHECKLIST_ITEM_MISMATCH',
  /**
   * Backstop for a race between two concurrent requests both opening an
   * asset_assignment row for the same asset — uq_asset_assignment_open.
   * 409 at the HTTP boundary. Mirrors rrhh's EMPLOYMENT_SCHEDULE_CONFLICT
   * (ADR D17). asset-assignment-history / migration 0118.
   */
  ASSET_ASSIGNMENT_CONFLICT: 'ASSET_ASSIGNMENT_CONFLICT',
  /**
   * Asset type is still referenced by at least one servicios.asset row
   * (asset_type_id). Deactivating/deleting it would silently orphan those
   * assets' type reference — the frontend falls back to showing the raw
   * asset_type_id UUID (AssetList.vue resolveTypeLabel()). Hard-delete/
   * deactivate is blocked; the operator must reassign the assets first.
   * 409 at the HTTP boundary. Production bug fix — tenant "BM Inspecciones",
   * assets BM-VLV-001/002/003, 2026-08-01.
   */
  ASSET_TYPE_IN_USE: 'ASSET_TYPE_IN_USE',
  /**
   * locx-permisos-de-trabajo PR1 (A1) — a ptw_type with the same code already
   * exists in this tenant scope (unique per companyId+code). Mirrors
   * FAILURE_TYPE_CODE_CONFLICT exactly. 409 at the HTTP boundary.
   */
  PTW_TYPE_CODE_CONFLICT: 'PTW_TYPE_CODE_CONFLICT',
  /**
   * locx-permisos-de-trabajo PR3 (B1) — a ptw_field_definition with the same
   * key already exists for this (ptwTypeId, ownerKind) tenant scope. Mirrors
   * FIELD_KEY_CONFLICT. 409 at the HTTP boundary.
   */
  PTW_FIELD_KEY_CONFLICT: 'PTW_FIELD_KEY_CONFLICT',
  /**
   * locx-permisos-de-trabajo PR5 (C) — a ptw_catalog_entry with the same
   * (kind, code) already exists in this tenant scope. Mirrors
   * PTW_TYPE_CODE_CONFLICT. 409 at the HTTP boundary.
   */
  PTW_CATALOG_ENTRY_CODE_CONFLICT: 'PTW_CATALOG_ENTRY_CODE_CONFLICT',
  /**
   * locx-permisos-de-trabajo PR5 (C), task 5.9 — a work_permit_document was
   * attached with a documentTypeId that does not resolve to an active
   * ptw_catalog_entry(kind='document_type') row in this tenant scope.
   * Caught at the APPLICATION layer (AttachWorkPermitDocumentUseCase) — never
   * left to the DB composite FK to reject as a raw foreign_key_violation.
   * 422 at the HTTP boundary.
   */
  PTW_UNKNOWN_DOCUMENT_TYPE: 'PTW_UNKNOWN_DOCUMENT_TYPE',
  /**
   * locx-permisos-de-trabajo PR5 (C) — domain/work-permit-approval.ts's
   * assertApprovable() rejected: at least one hazard→document requirement is
   * unmet (missing work_permit_document) or a required ATS is not linked.
   * 422 at the HTTP boundary. Consumed by TransitionWorkPermitUseCase (PR6).
   */
  PTW_APPROVAL_BLOCKED: 'PTW_APPROVAL_BLOCKED',
  /**
   * locx-permisos-de-trabajo PR5 (C) — LinkWorkPermitUseCase rejected a
   * self-link (fromPermitId === toPermitId), defense-in-depth ahead of the DB
   * CHECK (chk_work_permit_link_distinct, migration 0170). 422 at the HTTP boundary.
   */
  PTW_LINK_SELF_REFERENCE: 'PTW_LINK_SELF_REFERENCE',
  /**
   * locx-permisos-de-trabajo PR6 (D) — TransitionWorkPermitUseCase rejected a
   * transition into SUSPENDIDO/RECHAZADO/CANCELADO/CANCELADO_A_REPROGRAMAR
   * with no `reason`. Validated at the APPLICATION layer, not a DB CHECK
   * (task 6.4 — the requirement depends on `toState`'s NAME, which
   * work_permit_transition cannot see, it only stores the state's uuid).
   * 422 at the HTTP boundary.
   */
  PTW_REASON_REQUIRED: 'PTW_REASON_REQUIRED',
  /**
   * locx-permisos-de-trabajo PR6 (D) — TransitionWorkPermitUseCase rejected a
   * transition into CERRADO with no `closureKindId`. Same application-layer
   * posture as PTW_REASON_REQUIRED. 422 at the HTTP boundary.
   */
  PTW_CLOSURE_KIND_REQUIRED: 'PTW_CLOSURE_KIND_REQUIRED',
  /**
   * locx-permisos-de-trabajo PR6 (D) — a `closureKindId` was supplied that
   * does not resolve to an ACTIVE ptw_catalog_entry(kind='closure_kind') row
   * in this tenant scope. Caught at the APPLICATION layer
   * (PtwCatalogEntryRepository.findActiveById) BEFORE the transition insert —
   * never left to the DB composite FK to reject as a raw
   * foreign_key_violation. Mirrors PTW_UNKNOWN_DOCUMENT_TYPE. 422 at the HTTP boundary.
   */
  PTW_UNKNOWN_CLOSURE_KIND: 'PTW_UNKNOWN_CLOSURE_KIND',
  /**
   * locx-permisos-de-trabajo PR7 (E) — RecordShiftSignoffUseCase rejected a
   * `shiftIndex` outside `1..ptwType.shiftsPerDay`. Validated at the
   * APPLICATION layer, not a DB CHECK (the maximum is per-tenant/per-ptw_type
   * config). 422 at the HTTP boundary.
   */
  PTW_INVALID_SHIFT_INDEX: 'PTW_INVALID_SHIFT_INDEX',
  /**
   * locx-permisos-de-trabajo PR7 (E) — a shift signoff was recorded with an
   * `authorityRoleId` that does not resolve to an active
   * ptw_catalog_entry(kind='authority_role') row in this tenant scope.
   * Mirrors PTW_UNKNOWN_DOCUMENT_TYPE. 422 at the HTTP boundary.
   */
  PTW_UNKNOWN_AUTHORITY_ROLE: 'PTW_UNKNOWN_AUTHORITY_ROLE',
  /**
   * locx-permisos-de-trabajo PR7 (E) — a shift signoff already exists for the
   * same (workPermitId, periodDate, shiftIndex, authorityRoleId, direction)
   * casilla (uq_work_permit_shift_signoff, migration 0172). 409 at the HTTP
   * boundary.
   */
  PTW_SHIFT_SIGNOFF_ALREADY_RECORDED: 'PTW_SHIFT_SIGNOFF_ALREADY_RECORDED',
  /**
   * locx-permisos-de-trabajo PR7 (E) — an atmosphere reading value referenced
   * a `ptwFieldDefinitionId` that does not resolve to an active gas field
   * definition (groupKey='gas', dataType='number') in this tenant scope. 422
   * at the HTTP boundary.
   */
  PTW_UNKNOWN_GAS_FIELD_DEFINITION: 'PTW_UNKNOWN_GAS_FIELD_DEFINITION',
  /**
   * locx-permisos-de-trabajo PR7 (E) — the same `ptwFieldDefinitionId`
   * appeared more than once in a single atmosphere reading's `values[]`. 422
   * at the HTTP boundary.
   */
  PTW_DUPLICATE_GAS_READING: 'PTW_DUPLICATE_GAS_READING',
  /**
   * locx-permisos-de-trabajo PR8 (F) — AddCrewMemberUseCase's caller-supplied
   * `employmentId` does not resolve via IEmployeeLookup.lookupByEmploymentId
   * in this tenant scope. Rejection-worthy, not a fallback (design.md §Slice F).
   * 422 at the HTTP boundary.
   */
  PTW_UNKNOWN_EMPLOYMENT: 'PTW_UNKNOWN_EMPLOYMENT',
  /**
   * locx-ats-content-and-lifecycle PR1 (A) — CreateAtsUseCase (and, from a
   * later PR in this chain, UpdateAtsUseCase's workPermitId relink) rejected
   * a `workPermitId`/`ptwTypeId` pair whose ptwTypeId values don't match: the
   * linked (or to-be-linked) permit's own `ptwTypeId` must equal the ATS's.
   * 409 at the HTTP boundary. design.md §Contratos Zod / proposal.md decision (b).
   */
  PTW_TYPE_MISMATCH: 'PTW_TYPE_MISMATCH',
  /**
   * A write (PATCH content, relink workPermitId) or a second cancel was
   * attempted against an ATS that is already `cancelled = true`. 409 at the
   * HTTP boundary. design.md §D5 / spec "Cancellation Is One-Way".
   */
  ATS_CANCELLED: 'ATS_CANCELLED',
  /**
   * cross-tenant-kernel-mechanism PR8 — CreateWorkPermitUseCase rejected a
   * caller-supplied `counterpartyCompanyId` with no ACTIVE
   * kernel-tenant-partnership where the CALLING tenant is the owner side and
   * the given id is the counterparty. Real authorization boundary: a tenant
   * must not be able to expose its own permits to an arbitrary company by
   * just passing any UUID. 422 at the HTTP boundary (shape-valid UUID,
   * rejected on a business rule — same posture as PTW_UNKNOWN_EMPLOYMENT).
   */
  PTW_COUNTERPARTY_NOT_PARTNERED: 'PTW_COUNTERPARTY_NOT_PARTNERED',
  /**
   * locx-paridad-operativa-bramix Slice B — CreateFuelLoadUseCase rejected a
   * create where the receiver asset type requires a reading (hours and/or
   * km, per asset_type.tracksHours/tracksKm) that is missing from the
   * request AND no non-empty missing-reading justification was supplied.
   * Does NOT block the write when a justification IS supplied — see
   * domain/meter-requirements.ts's assertMeterReadings (MR-3.2). 422 at the
   * HTTP boundary.
   */
  METER_READING_REASON_REQUIRED: 'METER_READING_REASON_REQUIRED',
  /**
   * locx-voz-multi-intencion (D9.3) — caller does not hold the capability
   * required for the intent in the CONFIRM request body (falla →
   * locx.falla.crear; repuesto/servicio/servicio_futuro → locx.servicio.crear).
   * 403 at the HTTP boundary. Checked per-body-intent, never a single static
   * requireCapability(...).
   */
  VOICE_INTAKE_INTENT_NOT_ALLOWED: 'VOICE_INTAKE_INTENT_NOT_ALLOWED',
  /**
   * locx-voz-multi-intencion — confirm attempted on a voice_intake row not in
   * a confirmable status (classified, or needs_review with a transcript).
   * Covers the compare-and-set "someone already confirmed" case (D9.4) and an
   * outright invalid-state attempt alike — both map to the same 409, the
   * response body's dispatchedEntityKind/_id tells the client which.
   */
  VOICE_INTAKE_NOT_CONFIRMABLE: 'VOICE_INTAKE_NOT_CONFIRMABLE',
  /**
   * locx-voz-multi-intencion — intent named in the confirm/process response
   * is recognized by the classifier's closed vocabulary but not yet routed
   * to a domain destination in this increment (servicio/servicio_futuro are
   * parked at needs_review with this reason instead of silently misrouting —
   * see ProcessVoiceIntakeUseCase's docblock).
   */
  VOICE_INTAKE_INTENT_NOT_SUPPORTED: 'VOICE_INTAKE_INTENT_NOT_SUPPORTED',
} as const;

/**
 * service-order.ts
 * Task 5.11 — Shared Zod contracts for the Servicios (LOCX) module.
 *
 * Used by:
 *   - Backend (http layer): parse inbound requests, serialize responses.
 *   - Frontend (Vue components): type inference without duplicating shapes.
 *
 * ZERO Hono / Drizzle / infrastructure imports — SL-6.3 / SL-6-B.
 * This package must pass the `contracts-self-contained` depcruise rule.
 *
 * Spec: SL-6.1 / SL-6.2 / SL-6.3 / SL-6-B / design §5d
 */
import { z } from 'zod';
import { ServiceItemStatusSchema, EntryModeSchema, ItemCategorySchema } from './service-item.js';

// ---------------------------------------------------------------------------
// ServiceType contracts
// ---------------------------------------------------------------------------

/**
 * rentalKindSchema — closed vocabulary for service_type.rental_kind (SL-35.3).
 * Valid values: 'alquiler' | 'venta' | null.
 * Any other value is rejected with a ZodError at the application boundary
 * (SL-35-B). No DB write is made when validation fails.
 *
 * Design §3: rental_kind is the discriminator for capability composition.
 * alquiler → pricing + execution + return + sourcing-handoff
 * venta    → pricing + execution (NO return, NO sourcing-handoff)
 * null     → non-rental type; all rental capabilities OFF
 */
export const rentalKindSchema = z
  .enum(['alquiler', 'venta'], {
    errorMap: () => ({
      message: "rentalKind must be 'alquiler', 'venta', or null",
    }),
  })
  .nullable();

export type RentalKindContract = z.infer<typeof rentalKindSchema>;

// ---------------------------------------------------------------------------
// Service Profile contracts (locx-bm-rental-wizard design §5 / D1 / D2)
// ---------------------------------------------------------------------------

/**
 * serviceProfileSchema — closed vocabulary for service_type.service_profile.
 * Valid values: 'rental_managed_docs' | null. NULL = no special profile
 * (every tenant except the ones a profile was explicitly assigned to).
 *
 * D1 (design.md): the value names the PATTERN, never the client — see
 * packages/modules/servicios/SERVICE-PROFILES.md for the naming convention
 * and the full profile registry. 'bm_rental' (the proposal's placeholder) is
 * intentionally NOT a valid value.
 *
 * DB CHECK constraint in migration 0123 provides defense-in-depth backstop,
 * same three-point enforcement posture as rentalKindSchema.
 *
 * Spec: SP-1.1, SP-2.1
 */
export const serviceProfileSchema = z
  .enum(['rental_managed_docs'], {
    errorMap: () => ({
      message: "serviceProfile must be 'rental_managed_docs' or null",
    }),
  })
  .nullable();

export type ServiceProfileContract = z.infer<typeof serviceProfileSchema>;

// ---------------------------------------------------------------------------
// Milestone contracts (locx-bm-rental-wizard design §5 / D3-D6)
// ---------------------------------------------------------------------------

/**
 * milestoneKindSchema — closed vocabulary for service_order_milestone.kind
 * (design.md D5, confirmed decision 1). Grew from 5 to 6 values in item 5
 * point 4 (paso 5): `devolucion_proveedor` — "Devolución de BM al
 * Proveedor", only for third-party assets — is its own milestone, not a
 * sub-state of `devolucion`. A fixed, code-defined enum — there is NO
 * admin-maintained catalog for this vocabulary; a 7th kind is a future code
 * change (LOCX CLAUDE.md red lines #2/#3). Mirrors `MILESTONE_KINDS` in
 * `@suite/mod-servicios`'s `domain/milestone-kinds.ts` — kept in sync by
 * hand, both are pure code.
 *
 * Spec: SP-3.1, SP-3.2
 */
export const milestoneKindSchema = z.enum(
  ['presupuesto', 'oc_cliente', 'recepcion', 'entrega', 'devolucion', 'devolucion_proveedor'],
  {
    errorMap: () => ({
      message:
        "kind must be one of 'presupuesto', 'oc_cliente', 'recepcion', 'entrega', 'devolucion', 'devolucion_proveedor'",
    }),
  },
);

export type MilestoneKindContract = z.infer<typeof milestoneKindSchema>;

/**
 * estadoDevolucionSchema — closed 2-value vocabulary for
 * service_order_milestone.estado_devolucion (item 5 point 4, paso 5's
 * "Conforme/Disconforme"). Meaningful ONLY for the `devolucion` kind — NULL
 * on every other kind, including `devolucion_proveedor`. Mirrors
 * `ESTADO_DEVOLUCION_VALUES` in `@suite/mod-servicios`'s
 * `domain/estado-devolucion.ts`.
 *
 * NOT the same concept as paso 4's "Control del Estado" — that one stays
 * free text in `notes` (explicit decision, item 5 point 1).
 */
export const estadoDevolucionSchema = z.enum(['conforme', 'disconforme'], {
  errorMap: () => ({ message: "estadoDevolucion must be one of 'conforme', 'disconforme'" }),
});

export type EstadoDevolucionContract = z.infer<typeof estadoDevolucionSchema>;

/**
 * attachmentKindSchema — closed 8-value vocabulary for
 * service_order_milestone_attachment.kind (rental-requi-consolidado item 5).
 * Same posture as milestoneKindSchema: a fixed, code-defined enum, NO
 * admin-maintained catalog. A 9th kind is a future code change (LOCX
 * CLAUDE.md red lines #2/#3). Mirrors `ATTACHMENT_KINDS` in
 * `@suite/mod-servicios`'s `domain/attachment-kinds.ts` — kept in sync by
 * hand, both are pure code.
 *
 * `_doc` suffix is deliberate: `presupuesto`/`oc_cliente` are already
 * milestone kinds — an unsuffixed value would collide in meaning depending
 * on which table it's read from.
 */
export const attachmentKindSchema = z.enum(
  [
    'presupuesto_doc',
    'oc_cliente_doc',
    'oc_proveedor_doc',
    'remito_ingreso_doc',
    'remito_entrega_doc',
    'remito_devolucion_doc',
    'remito_devolucion_proveedor_doc',
    'foto',
  ],
  {
    errorMap: () => ({
      message:
        "kind must be one of 'presupuesto_doc', 'oc_cliente_doc', 'oc_proveedor_doc', 'remito_ingreso_doc', 'remito_entrega_doc', 'remito_devolucion_doc', 'remito_devolucion_proveedor_doc', 'foto'",
    }),
  },
);

export type AttachmentKindContract = z.infer<typeof attachmentKindSchema>;

/**
 * MilestoneCreateSchema — request body for `POST /servicios/orders/:id/milestones`
 * (design §6). Save-and-continue (SP-4): a milestone write is never required to
 * advance the wizard step it belongs to.
 *
 * `recordedBy` is deliberately NOT a field here — it is ALWAYS derived from
 * `TenantContext.userId` at write time (D4/SL-38.4-style posture), never from
 * the request body. Any `recordedBy` key present in an incoming payload is
 * simply ignored by `.parse()` (Zod strips unknown keys by default).
 *
 * `supplierId` is validated here only as a UUID shape; existence against
 * `sumi.supplier` is a write-time, application-layer check via
 * `ISupplierLookupPort` (design §7A step c) — not expressible in a pure schema.
 *
 * Spec: SP-4.1
 */
export const MilestoneCreateSchema = z.object({
  kind: milestoneKindSchema,
  occurredAt: z.string().datetime(),
  reference: z.string().max(120).optional(),
  notes: z.string().max(2000).optional(),
  supplierId: z.string().uuid().optional(),
  /** Meaningful only when kind === 'devolucion' (item 5 point 4). */
  estadoDevolucion: estadoDevolucionSchema.optional(),
  /**
   * SP-4.7-4.9 (migration 0151, locx-rental-contrato-alquiler Phase E) —
   * meaningful only when kind === 'devolucion'. Persisted verbatim to
   * `external_supplier`, NOT derived from asset.ownership.
   */
  externalSupplier: z.boolean().optional(),
});

export type MilestoneCreate = z.infer<typeof MilestoneCreateSchema>;

/**
 * AttachMilestonePhotosSchema — request body for
 * `POST /servicios/milestones/:id/attachments` (design §9, Phase 3 / PR C).
 * fileIds reference kernel files ALREADY uploaded via the existing
 * file-service (SP-5.1 — this introduces zero new upload/storage code).
 *
 * `kind` (item 5) — ALL fileIds in one call share the same attachment kind:
 * one call = one typed slot (e.g. "Remito de Entrega" or the shared "foto"
 * slot), never a mixed batch. Required, no server-side default.
 */
export const AttachMilestonePhotosSchema = z
  .object({
    fileIds: z.array(z.string().uuid()).min(1, 'At least one fileId is required'),
    kind: attachmentKindSchema,
  })
  .strict();

export type AttachMilestonePhotosInput = z.infer<typeof AttachMilestonePhotosSchema>;

/**
 * MilestoneAttachmentResponseSchema — one resolved photo/document attached to
 * a milestone (SP-5.1). Metadata resolved via the kernel's `lookupFileById`
 * (KI-8) — never duplicated here (KI-6.3). Same shape as
 * `DailyReportAttachmentResponseSchema` (daily-report.ts) — kept as its own
 * definition rather than a cross-import, mirroring how milestone contracts
 * already live standalone in this file (Phase 1).
 *
 * Spec: SP-5.1
 */
export const MilestoneAttachmentResponseSchema = z.object({
  id: z.string().uuid(),
  fileId: z.string().uuid(),
  kind: attachmentKindSchema,
  originalName: z.string(),
  mimeType: z.string(),
  sizeBytes: z.number(),
  createdAt: z.string().datetime().or(z.date()),
});

export type MilestoneAttachmentResponse = z.infer<typeof MilestoneAttachmentResponseSchema>;

/**
 * MilestoneResponseSchema — `GET /servicios/orders/:id/milestones` item shape
 * (design §6). `recordedBy`/`recordedByName` and `supplierId`/`supplierName`
 * are resolved read-side (kernel `lookupUserById` / `ISupplierLookup`) — the
 * raw ids stay on the row for callers that only need the reference, the
 * `*Name` fields are display-only convenience (mirrors `serviceTypeName`
 * alongside `serviceTypeId` elsewhere in this file).
 *
 * `attachments` is ALWAYS present (possibly `[]`) — no milestone can ever
 * lack the array itself, only its contents (D4/SP-5.1).
 *
 * Spec: SP-4.1, SP-5.1, design §6
 */
export const MilestoneResponseSchema = z.object({
  id: z.string().uuid(),
  companyId: z.string().uuid(),
  serviceOrderId: z.string().uuid(),
  kind: milestoneKindSchema,
  occurredAt: z.string().datetime().or(z.date()),
  reference: z.string().nullable(),
  notes: z.string().nullable(),
  supplierId: z.string().uuid().nullable(),
  supplierName: z.string().nullable(),
  /** Meaningful only when kind === 'devolucion' (item 5 point 4); NULL elsewhere. */
  estadoDevolucion: estadoDevolucionSchema.nullable(),
  /**
   * SP-4.7-4.9 (migration 0151) — the operator's explicit "¿proveedor
   * externo?" answer, meaningful only when kind === 'devolucion'. `null` is
   * "not yet answered", distinct from an explicit `false` — MUST round-trip.
   */
  externalSupplier: z.boolean().nullable(),
  recordedBy: z.string().uuid(),
  recordedByName: z.string().nullable(),
  createdAt: z.string().datetime().or(z.date()),
  attachments: z.array(MilestoneAttachmentResponseSchema),
});

export type MilestoneResponse = z.infer<typeof MilestoneResponseSchema>;

// ---------------------------------------------------------------------------
// Pricing contracts (SL-37 — locx-rental-bm Slice 4)
// ---------------------------------------------------------------------------

/**
 * rateTypeSchema — closed vocabulary for service_order_asset.rate_type (SL-37.2).
 * Valid values: 'daily' | 'weekly' | 'monthly' | 'flat' | null.
 * Any other value is rejected with a ZodError at the application boundary.
 * DB CHECK constraint in migration 0028 provides defense-in-depth backstop.
 *
 * Spec: SL-37.2, SL-37.5
 */
export const rateTypeSchema = z
  .enum(['daily', 'weekly', 'monthly', 'flat'], {
    errorMap: () => ({
      message: "rateType must be 'daily', 'weekly', 'monthly', 'flat', or null",
    }),
  })
  .nullable();

export type RateTypeContract = z.infer<typeof rateTypeSchema>;

/**
 * AssetPricingSchema — optional pricing fields for an asset link (SL-37.5).
 * All three fields are nullable — present only for rental service types.
 *
 * unitPrice: numeric string (e.g. '1500.00') validated as a decimal pattern.
 * rateType:  closed vocab daily|weekly|monthly|flat or null.
 * currency:  ISO-4217-ish text (e.g. 'ARS', 'USD'); max 10 chars to prevent abuse.
 *
 * When the service type has pricing capability ON, unit_price + rate_type MUST
 * be non-null — enforced at the application layer in LinkAssetToOrderUseCase.
 *
 * Spec: SL-37.1, SL-37.2, SL-37.3, SL-37.4, SL-37.5
 */
export const AssetPricingSchema = z.object({
  unitPrice: z
    .string()
    .regex(/^\d+(\.\d+)?$/, 'unitPrice must be a numeric string (e.g. "1500.00")')
    .nullable()
    .optional()
    .transform((v) => v ?? null),
  rateType: rateTypeSchema.optional().transform((v) => v ?? null),
  currency: z
    .string()
    .min(1, 'currency must not be empty when provided')
    .max(10, 'currency must be at most 10 characters')
    .nullable()
    .optional()
    .transform((v) => v ?? null),
});

export type AssetPricingContract = z.infer<typeof AssetPricingSchema>;

// ---------------------------------------------------------------------------
// Workflow derivation helper
// ---------------------------------------------------------------------------

/**
 * Closed set of FSM workflow names used by the Servicios module.
 * 'service_order'        — base workflow (non-rental; NULL rental_kind).
 * 'service_order_rental' — alquiler workflow (return path required).
 * 'service_order_venta'  — venta workflow (execution→completed, no return).
 *
 * Reference for callers that need to enumerate valid workflow keys without
 * depending on application-layer logic.
 */
export const SERVICE_ORDER_WORKFLOWS = [
  'service_order',
  'service_order_rental',
  'service_order_venta',
] as const;

export type ServiceOrderWorkflow = (typeof SERVICE_ORDER_WORKFLOWS)[number];

/**
 * deriveWorkflow — pure mapping from rentalKind to FSM workflow key.
 *
 * alquiler  → 'service_order_rental'  (return-mandatory path)
 * venta     → 'service_order_venta'   (execution→completed, no return)
 * null      → null                    (base 'service_order' workflow; stored as NULL)
 * undefined → null                    (treated same as null — caller omitted rentalKind)
 *
 * The backend stores NULL in service_type.workflow for non-rental types;
 * WorkflowResolver falls back to 'service_order' when workflow IS NULL.
 *
 * This is a PURE function — no side effects, no DB access.
 */
export function deriveWorkflow(
  rentalKind: 'alquiler' | 'venta' | null | undefined,
): string | null {
  if (rentalKind === 'alquiler') return 'service_order_rental';
  if (rentalKind === 'venta') return 'service_order_venta';
  return null;
}

/**
 * Request body for creating a new service type (admin only).
 * Service types are LOCX-owned (servicios schema — CHANGE A / SL-2.3).
 *
 * rentalKind: optional — when omitted the type is non-rental (workflow = null).
 *   'alquiler' → seeds rental FSM + sets workflow = 'service_order_rental'.
 *   'venta'    → seeds venta FSM  + sets workflow = 'service_order_venta'.
 *   null       → explicit non-rental (same as omitting; included for completeness).
 *
 * workflow is NOT accepted from the body — it is derived server-side from rentalKind
 * via deriveWorkflow() and written atomically in the same transaction.
 *
 * Mirrors ClientTypeCreate in contracts-kernel pattern.
 */
/**
 * DefaultItemTemplateSchema — one row of a service_type's default_items template
 * (SL-46.1, SL-46.2). Closed shape: no conditionals/formulas allowed (LOCX
 * CLAUDE.md red lines #1/#2) — pure data the item-instantiation use-case (Slice 3)
 * copies onto new service_item rows.
 *
 * Spec: SL-46.2
 */
export const DefaultItemTemplateSchema = z
  .object({
    code: z.string().min(1, 'code is required').max(64, 'code must be at most 64 characters'),
    name: z.string().min(1, 'name is required').max(256, 'name must be at most 256 characters'),
    unit: z.string().min(1, 'unit is required').max(32, 'unit must be at most 32 characters'),
    requiresQualityGate: z.boolean(),
    /**
     * category (2026-07-21): optional, backward-compatible — drives which
     * daily-report line section(s) (Transporte/Equipos/Personal) the
     * instantiated item is offered in (see ItemCategorySchema). Templates
     * saved before this field existed simply omit it — treated downstream as
     * "unclassified, offered everywhere", so nothing already configured
     * breaks. New/edited templates should set it.
     */
    category: ItemCategorySchema.optional(),
  })
  .strict();

export type DefaultItemTemplate = z.infer<typeof DefaultItemTemplateSchema>;

/**
 * DefaultItemsSchema — the full template array on a service_type.
 * Empty array / absent = no template (SL-47.3 backward-compat).
 *
 * Spec: SL-46.1, SL-46.2
 */
export const DefaultItemsSchema = z.array(DefaultItemTemplateSchema).max(50, 'at most 50 default items');

export type DefaultItems = z.infer<typeof DefaultItemsSchema>;

export const ServiceTypeCreateSchema = z.object({
  // Naming standard: code becomes the order-number prefix verbatim (SO-<code>-NNN,
  // CreateServiceOrderUseCase) — a long code (e.g. "MOVIMIENTOSUELO") makes every
  // order number for that type unreadable. 24 is a hard ceiling (loose enough not to
  // break existing codes like RENTAL_ALQUILER); the admin UI additionally recommends
  // ≤10 chars and previews the resulting order-number format so short codes are the
  // path of least resistance, without forcing a breaking rename of existing types.
  code: z.string().min(1, 'code is required').max(24, 'code must be at most 24 characters'),
  label: z.string().min(1, 'label is required').max(256, 'label must be at most 256 characters'),
  rentalKind: rentalKindSchema.optional(),
  // SL-46.1: optional template of certifiable items instantiated at order-creation
  // time (Slice 3). Absent/empty = no template (backward-compat).
  defaultItems: DefaultItemsSchema.optional(),
  // SL-50.1: dailyReport capability flag — independent of rentalKind (add-locx-daily-report
  // design §2). Defaults to false when omitted (non-breaking for existing callers).
  dailyReportEnabled: z.boolean().optional(),
});

export type ServiceTypeCreate = z.infer<typeof ServiceTypeCreateSchema>;

/**
 * Response shape for a service type.
 * id + code + label + active + companyId (null for system-wide).
 * rentalKind: nullable — null for non-rental types (SL-35.4).
 * defaultItems: nullable — null when the type has no progress-tracking template.
 * dailyReportEnabled: boolean — SL-50.1, independent of rentalKind.
 */
export const ServiceTypeResponseSchema = z.object({
  id: z.string().uuid(),
  companyId: z.string().uuid().nullable(),
  code: z.string(),
  label: z.string(),
  active: z.boolean(),
  // SL-35.4: rentalKind is included in the response; null = non-rental type.
  rentalKind: rentalKindSchema,
  // SL-46.1: null = no default_items template configured for this type.
  defaultItems: DefaultItemsSchema.nullable().optional(),
  // SL-50.1: dailyReport capability flag — independent of rentalKind.
  dailyReportEnabled: z.boolean(),
  /**
   * locx-bm-rental-wizard Phase 1 (design §5): closed-vocab service profile,
   * null = no special profile. `.optional()` — same backward-compat posture
   * as `defaultItems` above: ServiceTypeRepository is wired to select/return
   * this column in a later phase of this same change (Phase 2); until then,
   * existing response-building call sites that predate this field keep
   * compiling unmodified (SP-2.2-style regression guard applied to the
   * contract shape, not just the pure function).
   */
  serviceProfile: serviceProfileSchema.optional(),
});

export type ServiceTypeResponse = z.infer<typeof ServiceTypeResponseSchema>;

// ---------------------------------------------------------------------------
// ServiceOrder contracts
// ---------------------------------------------------------------------------

/**
 * One line item in a service order.
 * Optional collection on create — orders can start without lines.
 */
export const ServiceOrderLineInputSchema = z.object({
  description: z.string().min(1, 'description is required'),
  quantity: z.string().regex(/^\d+(\.\d+)?$/, 'quantity must be a numeric string').optional().default('1'),
  unit: z.string().optional(),
});

export type ServiceOrderLineInput = z.infer<typeof ServiceOrderLineInputSchema>;

/**
 * Primitive value union for a dynamic field on the wire (closed by data_type).
 * date/datetime arrive as ISO strings; number/integer/money as numbers; boolean as boolean; text/select as string.
 * The real per-field validation (type, constraints, required, unknown-key rejection)
 * happens inside the use-case via DynamicZodBuilder — the outer contract only guarantees
 * "a flat bag of primitives". SL-13.1, SL-13.6.
 */
export const DynamicFieldValueSchema = z.union([
  z.string(),   // text, select, date (ISO), datetime (ISO)
  z.number(),   // number, integer, money
  z.boolean(),  // boolean
]);

export type DynamicFieldValue = z.infer<typeof DynamicFieldValueSchema>;

/**
 * Request body for creating a new Service Order (SL-4.1 / SL-6.1).
 *
 * tenantId is NEVER here — it comes from TenantContext (KC-3.5).
 * clientId and serviceTypeId are validated by the application use-case
 * via kernel lookup ports / module repos.
 *
 * fields: optional bag of dynamic field values keyed by field.key.
 * Validated server-side against the resolved field definitions for serviceTypeId
 * (DynamicZodBuilder). Backward-compatible — existing callers that omit fields are unaffected.
 * SL-13.1, SL-13.6.
 */
export const ServiceOrderCreateSchema = z.object({
  clientId: z.string().uuid('clientId must be a valid UUID'),
  serviceTypeId: z.string().uuid('serviceTypeId must be a valid UUID'),
  // orderNumber is NOT accepted here — auto-generated server-side as
  // "SO-<PREFIX>-NNN" (one counter per company + service_type). See
  // CreateServiceOrderUseCase Step 5c.
  wellName: z.string().max(256).optional(),
  location: z.string().max(512).optional(),
  notes: z.string().optional(),
  /** → catalog.cost_center (validated on write, same posture as clientId). */
  costCenterId: z.string().uuid('costCenterId must be a valid UUID').optional(),
  assignedUserId: z.string().uuid().optional(),
  lines: z.array(ServiceOrderLineInputSchema).optional(),
  /** Dynamic field values keyed by field.key. Validated server-side against resolved defs. */
  fields: z.record(z.string(), DynamicFieldValueSchema).optional(),
  /**
   * → identity.organization ("Razón Social") — which legal entity delivers/bills this
   * order (merge-holding-group-tenant design.md §1/§2). OPTIONAL at the contract level:
   * identity.organization is global (no company_id), so most tenants (single-firma,
   * no holding group) have nothing to pick and this stays null forever for them. The
   * frontend only shows the picker (and treats it as required) when the tenant's group
   * has more than one Razón Social to choose from — see
   * GET /identity/organizations/for-tenant-group.
   */
  legalEntityId: z.string().uuid('legalEntityId must be a valid UUID').optional(),
});

export type ServiceOrderCreate = z.infer<typeof ServiceOrderCreateSchema>;

/**
 * Request body for updating a DRAFT service order's base fields (fix/rental-locx-bm
 * FASE 1.3 — "Editar" reopens the rental wizard's step 1 for an existing order).
 *
 * serviceTypeId is intentionally OMITTED — changing the type of an in-flight order
 * has no defined semantics (different step set, different dynamic fields) and is
 * not supported. Every field is optional: only the keys present in the body are
 * updated (UpdateServiceOrderUseCase), the rest keep their current stored value.
 */
export const ServiceOrderUpdateSchema = z.object({
  clientId: z.string().uuid('clientId must be a valid UUID').optional(),
  wellName: z.string().max(256).optional(),
  location: z.string().max(512).optional(),
  notes: z.string().optional(),
  costCenterId: z.string().uuid('costCenterId must be a valid UUID').optional(),
  legalEntityId: z.string().uuid('legalEntityId must be a valid UUID').optional(),
  fields: z.record(z.string(), DynamicFieldValueSchema).optional(),
  /**
   * 2026-08-28 — explicit opt-in to the lighter "Editar datos" guard
   * (editable through IN_PROGRESS, ignores linked assets/milestones/
   * requisitions) instead of the strict wizard-reopen guard (DRAFT + zero
   * downstream data). See UpdateServiceOrderUseCase's docblock. Defaults to
   * false/omitted — every existing caller keeps the original behavior.
   */
  metadataOnly: z.boolean().optional(),
});

export type ServiceOrderUpdate = z.infer<typeof ServiceOrderUpdateSchema>;

/**
 * Response shape for a Service Order.
 *
 * currentState: the RESOLVED state NAME (e.g. 'DRAFT', 'SUBMITTED') derived from
 *   service_order.current_state_id → identity.state.name at read time.
 *   It is NOT a stored text column (design §5c / review BLOCKING #1).
 *
 * currentStateId: the raw UUID reference to identity.state — included for
 *   advanced consumers that need to correlate with the state table.
 */
export const ServiceOrderResponseSchema = z.object({
  id: z.string().uuid(),
  companyId: z.string().uuid(),
  clientId: z.string().uuid(),
  serviceTypeId: z.string().uuid(),
  assignedUserId: z.string().uuid().nullable(),
  currentStateId: z.string().uuid(),
  currentState: z.string(), // resolved state NAME (e.g. 'DRAFT') — SL-4-D note
  orderNumber: z.string(),
  wellName: z.string().nullable(),
  location: z.string().nullable(),
  notes: z.string().nullable(),
  costCenterId: z.string().uuid().nullable(),
  /** → identity.organization ("Razón Social"). Null for single-firma tenants and for
   *  rows created before this field existed (backfill is gradual, not day-1). */
  legalEntityId: z.string().uuid().nullable(),
  createdAt: z.string().datetime().or(z.date()),
  updatedAt: z.string().datetime().or(z.date()),
  /**
   * fix/rental-editar-guard: server-computed — true only when the rental
   * wizard's "Editar" action (reopen at step 1) is safe, i.e. the order is
   * DRAFT, rental (rentalKind !== null), AND has produced ZERO downstream
   * data yet (no linked assets, no recorded milestones, no linked purchase
   * requisitions). The wizard's steps 2-5 never transition the order out of
   * DRAFT (only the first "parte de avance" does), so `currentState ===
   * 'DRAFT'` alone is NOT sufficient — an order that completed the entire
   * wizard can still be DRAFT. Reopening the wizard has no prefill for
   * assets/milestones/requisitions, so doing it on such an order duplicates
   * them. See packages/modules/servicios/src/domain/service-order-editability.ts
   * (the single source of truth for this rule) — every caller (this
   * contract, the board row below, and UpdateServiceOrderUseCase's
   * write-time guard) composes the same booleans and calls it.
   */
  canEdit: z.boolean(),
  /**
   * 2026-08-28 — server-computed — true when a LIGHTWEIGHT metadata edit
   * (client/wellName/location/notes/costCenterId/legalEntityId via
   * `PATCH .../orders/:id` with `metadataOnly: true`) is allowed. Separate
   * from `canEdit` (which gates reopening the FULL wizard): this one is NOT
   * blocked by linked assets/milestones/requisitions — a metadata patch
   * never touches those tables — only by FSM state. Editable through
   * IN_PROGRESS; locked from EXECUTION onward. See
   * computeServiceOrderMetadataCanEdit (service-order-editability.ts).
   */
  canEditMetadata: z.boolean(),
});

export type ServiceOrderResponse = z.infer<typeof ServiceOrderResponseSchema>;

/**
 * One dynamic field value as returned in the service order detail response (SL-14.1, SL-14.4).
 * Typed on the wire — no raw value_* keys ever appear. key/label/dataType come from
 * the resolved field definition; value is the typed runtime value or null when not set.
 */
export const ServiceOrderFieldValueViewSchema = z.object({
  key: z.string(),
  label: z.string(),
  dataType: z.enum(['text', 'number', 'integer', 'boolean', 'date', 'datetime', 'money', 'select']),
  value: z.union([z.string(), z.number(), z.boolean(), z.null()]),
});

export type ServiceOrderFieldValueView = z.infer<typeof ServiceOrderFieldValueViewSchema>;

/**
 * Request body for transitioning a Service Order to a new state (SL-4.2 / SL-4.3 / SL-43).
 *
 * toState: open string (SL-43) — widened from the closed 4-state enum so rental workflow
 * states (EXECUTION, RETURN_REQUESTED, RETURNED, DISCONFORMITY) are accepted.
 * The server (catalog FSM via WorkflowResolver + TransitionServiceOrderUseCase) is the
 * source of truth and validates the edge; the contract only ensures the string is non-empty.
 *
 * Backward-compatible: the base 4 states (SUBMITTED, IN_PROGRESS, COMPLETED, CANCELLED)
 * are valid non-empty strings. Existing callers are unaffected.
 *
 * Declared before ServiceOrderDetailResponseSchema so availableTransitions is typed consistently.
 */
export const ServiceOrderTransitionSchema = z.object({
  toState: z.string().min(1, { message: 'toState must be a non-empty string' }),
});

export type ServiceOrderTransition = z.infer<typeof ServiceOrderTransitionSchema>;

/**
 * Extended response for GET /servicios/orders/:id — base fields + resolved dynamic fields
 * + available FSM transitions.
 *
 * availableTransitions: the next states the order can move to from its currentState.
 *   Derived server-side from serviceOrderFsm.transitions[currentState] so the frontend
 *   never duplicates the FSM transition map. Empty for terminal states (COMPLETED, CANCELLED).
 *   SL-4.3 / SL-4.4.
 *
 * fields: typed values assembled from ServiceOrderFieldValueRepository + FieldResolver.
 *   EAV rows never appear. Empty when the service type has no visible fields or none were stored.
 *   SL-14.1, SL-14.4.
 */
/**
 * ExecutionSummarySchema — minimal execution presence shape for GET /orders/:id (FS-48 V.2).
 * Exposes the most-recent execution record so the detail view knows whether execution
 * was recorded (and can show the summary or the inline ExecutionStep form).
 *
 * Additive and backward-compatible: absent from any existing response consumer that
 * does not reference the field — TypeScript spread and object destructuring are safe.
 *
 * Spec: FS-48 / design §2 V.2 gap
 */
export const ExecutionSummarySchema = z.object({
  id: z.string().uuid(),
  executedAt: z.string().datetime().or(z.date()),
  remitoNumber: z.string().nullable(),
}).nullable();

export type ExecutionSummary = z.infer<typeof ExecutionSummarySchema>;

/**
 * ReturnSummarySchema — minimal return presence shape for GET /orders/:id (FS-49 V.2).
 * Exposes the most-recent return record so the detail view knows whether a return was
 * recorded (and can show the summary or the inline ReturnStep form).
 *
 * alquiler only: venta orders never have returns. The frontend uses rentalKind to gate
 * the return section; returnSummary is always null for non-alquiler orders.
 *
 * Spec: FS-49 / design §2 V.2 gap
 */
export const ReturnSummarySchema = z.object({
  id: z.string().uuid(),
  returnedAt: z.string().datetime().or(z.date()),
  hasDisconformity: z.boolean(),
}).nullable();

export type ReturnSummary = z.infer<typeof ReturnSummarySchema>;

/**
 * OrderAvanceItemViewSchema — a single service_item projected with its
 * physical/financial progress for the order detail response (Slice 5, SL-49.1).
 */
export const OrderAvanceItemViewSchema = z.object({
  id: z.string().uuid(),
  code: z.string(),
  name: z.string(),
  unit: z.string(),
  qtyTotal: z.string().nullable(),
  qtyDone: z.string(),
  unitPrice: z.string().nullable(),
  status: ServiceItemStatusSchema,
  physicalProgress: z.number().nullable(),
  financialProgress: z.number().nullable(),
  /**
   * SL-60.1: Path A ('absolute') vs Path B ('delta') discriminator — lets the daily-report
   * frontend render a direct avance-matrix input only for 'absolute' items.
   */
  entryMode: EntryModeSchema,
  /**
   * category (2026-07-21): null for items instantiated before this field existed
   * (or never classified) — the daily-report wizard treats null as "offered in
   * every line section", matching prior behavior exactly. See ItemCategorySchema.
   */
  category: ItemCategorySchema.nullable(),
});

export type OrderAvanceItemView = z.infer<typeof OrderAvanceItemViewSchema>;

/**
 * OrderAvanceAggregateSchema — order-level rollup (SL-49.2). null for orders
 * with zero service_item rows (non-progress-tracked types, SL-49.4).
 */
export const OrderAvanceAggregateSchema = z
  .object({
    aggregatePhysicalProgress: z.number().nullable(),
    aggregateFinancialProgress: z.number(),
  })
  .nullable();

export type OrderAvanceAggregate = z.infer<typeof OrderAvanceAggregateSchema>;

/**
 * LinkedRequisitionViewSchema — a single compras.purchase_cases row linked to
 * this service order via source_service_order_id ("Solicitar compra" results).
 * add-requi-manual-purchase-request follow-up.
 *
 * locx-special-service-profiles, design.md D6 / SL-81.1: `priority` is DROPPED
 * — `purchase_cases` has no priority column, and zero `.vue` consumers
 * rendered it (verified at design time).
 */
export const LinkedRequisitionViewSchema = z.object({
  id: z.string().uuid(),
  requisitionNumber: z.string(),
  currentState: z.string(),
  createdAt: z.string(),
});

export type LinkedRequisitionView = z.infer<typeof LinkedRequisitionViewSchema>;

export const ServiceOrderDetailResponseSchema = ServiceOrderResponseSchema.extend({
  /**
   * Ficha redesign (2026-07-21): resolved service_type label, shown as a badge in the
   * detail header. Sourced from the same intra-servicios JOIN as rentalKind/
   * dailyReportEnabled (ServiceOrderRepository.findById). Orphan FK (type not found)
   * degrades to null rather than a hard error — read-only display data.
   */
  serviceTypeName: z.string().nullable(),
  fields: z.array(ServiceOrderFieldValueViewSchema),
  /**
   * Derived from ServiceOrderTransitionSchema.shape.toState — single source of truth.
   * SL-43: now an open z.string(), so any catalog FSM state name is accepted on the wire.
   * The server populates this from availableTransitions via WorkflowResolver — the frontend
   * never hard-codes the set. Empty for terminal states. SL-4.3 / SL-4.4 / SL-43.
   */
  availableTransitions: z.array(ServiceOrderTransitionSchema.shape.toState),
  /**
   * SL-42: rentalKind exposed on the order detail — sourced from the order's service_type.
   * Enables the frontend detail view to show rental-specific UI (execution/return panels)
   * without a separate endpoint call. null = non-rental service type.
   */
  rentalKind: rentalKindSchema,
  /**
   * SL-59.1: dailyReportEnabled exposed on the order detail — sourced from the order's
   * service_type (same capability flag SL-50's resolveCapabilities reads). Mirrors SL-42's
   * rentalKind exposure exactly: lets the frontend decide whether to render the "Partes
   * diarios" section without a separate call.
   */
  dailyReportEnabled: z.boolean(),
  /**
   * passengersEnabled (2026-07-21): gates the Pasajeros section in the ficha — sourced
   * from the order's service_type (same LEFT JOIN as rentalKind/dailyReportEnabled).
   * Was unconditional before; most service types never carry passengers.
   */
  passengersEnabled: z.boolean(),
  /**
   * locx-bm-rental-wizard Phase 1 (design §5, verify fix): closed-vocab
   * service profile sourced from the order's service_type, same slot AND same
   * required-nullable posture as rentalKind/dailyReportEnabled/
   * passengersEnabled — NOT `.optional()`. `servicios.routes.ts`'s
   * `detailResponse` builder and `ServiceOrderRepository.findById`'s LEFT
   * JOIN always populate this (null-safe default null for no-profile types
   * or an orphan FK), so there is no backward-compat reason left to keep it
   * optional (see ServiceTypeReadItemSchema for the sibling fix).
   */
  serviceProfile: serviceProfileSchema,
  /**
   * FS-48 V.2: most-recent execution record for this order, or null when none exists.
   * Populated from service_order_execution by the detail handler via ExecutionReadPort.
   * When non-null, the detail view shows the execution summary; when null, it shows the
   * inline ExecutionStep form (for rental orders).
   * Null for non-rental orders and for rental orders with no recorded execution.
   */
  executionSummary: ExecutionSummarySchema,
  /**
   * FS-49 V.2: most-recent return record for this order, or null when none exists.
   * Populated from service_order_return by the detail handler via ReturnReadPort.
   * When non-null, the detail view shows the return summary; when null, it shows the
   * inline ReturnStep form (for alquiler orders with execution already recorded).
   * Always null for non-alquiler orders.
   */
  returnSummary: ReturnSummarySchema,
  /**
   * Slice 5 (SL-49.1, SL-49.4): certifiable items with per-item avance, empty
   * array for orders whose service_type has no default_items template
   * (fully backward-compatible with every response consumer that pre-dates this).
   */
  items: z.array(OrderAvanceItemViewSchema),
  /**
   * Slice 5 (SL-49.2, SL-49.4): order-level avance rollup. null when items is empty.
   */
  avance: OrderAvanceAggregateSchema,
  /**
   * add-requi-manual-purchase-request follow-up: purchase requests ("Solicitar
   * compra") already generated for this order — zero, one, or several (a
   * repeatable action). Empty array when none exist or the cross-module read
   * port isn't wired (backward-compatible).
   */
  linkedRequisitions: z.array(LinkedRequisitionViewSchema),
  /**
   * add-locx-order-detail-redesign, fase 5: display name for `assignedUserId`,
   * resolved server-side via the kernel's lookupUserById lookup port. null when
   * assignedUserId is null, or when the referenced user no longer resolves
   * (e.g. removed) — never a hard error, this is read-only display data.
   */
  assignedUserName: z.string().nullable(),
  /**
   * locx-bm-rental-wizard-ficha-fix: display name for `clientId`, resolved
   * server-side via the kernel's lookupClientById lookup port — same
   * null-safe posture and same single-row lookup pattern as
   * assignedUserName above (clientId itself is NOT nullable on the order,
   * but the lookup can still miss on an orphan/cross-tenant FK, so this
   * stays nullable rather than a hard error). Read-only display data, used
   * by RentalManagedDocsSummary.vue's "Cliente" field.
   */
  clientName: z.string().nullable(),
  /**
   * fix/rental-locx-bm-created-by: `service_order.created_by` (migration 0155).
   * Nullable for orders created before this column existed (no backfill source
   * — see the migration header) and for any order whose creator id no longer
   * resolves (removed user, cross-tenant orphan) — never a hard error.
   */
  createdBy: z.string().uuid().nullable(),
  /**
   * Display name for `createdBy`, resolved server-side via the kernel's
   * lookupUserById lookup port — same null-safe posture and same single-row
   * lookup pattern as assignedUserName/clientName above. null when createdBy
   * is null (historical order) or when the referenced user no longer resolves.
   */
  createdByName: z.string().nullable(),
});

export type ServiceOrderDetailResponse = z.infer<typeof ServiceOrderDetailResponseSchema>;

/**
 * Lean service type read item for the tenant picker (SL-24.4 / SL-42).
 * Contains only what the frontend service-type picker needs — no admin fields.
 *
 * SL-42: rentalKind added so the wizard can branch on rental kind at list time
 * without a separate per-type endpoint call. null = non-rental type.
 * Additive: existing consumers that spread or destructure this shape are unaffected
 * because they do not reference the new field.
 */
export const ServiceTypeReadItemSchema = z.object({
  id: z.string().uuid(),
  code: z.string(),
  label: z.string(),
  /** SL-42: rental discriminator — 'alquiler' | 'venta' | null */
  rentalKind: rentalKindSchema,
  /**
   * SL-46.1 — the certifiable-items template, so the order-creation form can
   * preview what will be instantiated. null = no progress-tracking template.
   */
  defaultItems: DefaultItemsSchema.nullable().optional(),
  /**
   * locx-bm-rental-wizard Phase 1 (verify fix): closed-vocab service profile —
   * REQUIRED (nullable), same posture as rentalKind above, NOT `.optional()`
   * like the ServiceTypeResponseSchema slot — `GET /servicios/order-options/
   * service-types` always sets this from the column (see
   * configurable-engine.routes.ts), so this is the ONE data path WizardShell
   * (FS-41.4) has to learn a service type's profile at list time.
   */
  serviceProfile: serviceProfileSchema,
});

export type ServiceTypeReadItem = z.infer<typeof ServiceTypeReadItemSchema>;

/**
 * List response for GET /servicios/order-options/service-types (SL-24.4).
 * Returns only active service types visible to the current tenant.
 */
export const ServiceTypeListResponseSchema = z.object({
  items: z.array(ServiceTypeReadItemSchema),
});

export type ServiceTypeListResponse = z.infer<typeof ServiceTypeListResponseSchema>;

// ---------------------------------------------------------------------------
// Service Order Board contracts — unified management board view
// ---------------------------------------------------------------------------

/**
 * A single row in the unified service-management board.
 *
 * Composed in apps/backend/src/views/ via port composition (Pattern 1):
 *   - serviceTypeId + serviceTypeName: from LocxQueryPort.listServiceOrders
 *     (resolved intra-servicios via same-schema JOIN on service_type)
 *   - clientId + clientName: from ClientQueryPort.getClientsByIds
 *     (resolved via kernel identity, tenant-scoped)
 *
 * clientName / currentState are nullable:
 *   - clientName is null when the client id has no matching identity.client row
 *     (data-integrity gap, not a hard error at the board level).
 *   - currentState is null when lookupStateById cannot resolve the stateId UUID
 *     (data-integrity gap; degrades gracefully to null instead of showing a raw UUID).
 *
 * NOTE: dynamic per-type field columns are deferred. This row contains only
 * the common columns visible for ALL service types (N° · Servicio · Cliente
 * · Estado · Fecha). The board is designed to accept extra columns later.
 */
export const ServiceOrderBoardRowSchema = z.object({
  id: z.string().uuid(),
  orderNumber: z.string(),
  serviceTypeId: z.string().uuid(),
  serviceTypeName: z.string(),
  clientId: z.string().uuid(),
  /** Resolved client name, or null when the client record is not found. */
  clientName: z.string().nullable(),
  /** → catalog.cost_center, null when the order has none assigned. */
  costCenterId: z.string().uuid().nullable(),
  /** Resolved cost-center name, or null (no cost center / lookup miss). */
  costCenterName: z.string().nullable(),
  /** Short alias code (e.g. "SER007") shown as the "CC" column — or null. */
  costCenterLabel: z.string().nullable(),
  /**
   * Resolved FSM state name (e.g. 'DRAFT'), or null when the state id cannot
   * be resolved via lookupStateById (data-integrity gap).
   */
  currentState: z.string().nullable(),
  createdAt: z.string().datetime().or(z.date()),
  /**
   * fix/rental-locx-bm FASE 1.3: rental discriminator resolved from the row's
   * service_type (same join as serviceTypeName/clientName above) — lets the
   * board gate the "Editar" row action to rental orders only (the wizard
   * edit flow only exists for rentalKind !== null). null = non-rental type.
   */
  rentalKind: rentalKindSchema,
  /**
   * fix/rental-editar-guard: same server-computed flag as
   * ServiceOrderResponseSchema.canEdit — see that field's docblock. The
   * board is a SEPARATE composition (apps/backend/src/views/
   * service-orders-board.ts, not ServiceOrderResponseSchema-derived), so it
   * is declared independently here rather than inherited.
   */
  canEdit: z.boolean(),
});

export type ServiceOrderBoardRow = z.infer<typeof ServiceOrderBoardRowSchema>;

/**
 * Closed enum of valid FSM state names for the service_order workflow.
 * Used in ServiceOrderBoardFilterSchema.status to produce a clean 422 at the
 * HTTP boundary when an invalid state name is sent — prevents raw strings
 * from ever reaching the DB.
 */
export const SERVICE_ORDER_FSM_STATES = ['DRAFT', 'SUBMITTED', 'IN_PROGRESS', 'COMPLETED', 'CANCELLED'] as const;
export type ServiceOrderFsmState = (typeof SERVICE_ORDER_FSM_STATES)[number];

/**
 * Query filters for the board endpoint (GET /views/service-orders).
 * All optional — an empty filter returns all orders for the tenant (paginated).
 *
 * status: closed enum of valid FSM state names (validated at the HTTP boundary
 * → 422 for unknown values, not a DB error).
 */
export const ServiceOrderBoardFilterSchema = z.object({
  serviceTypeId: z.string().uuid().optional(),
  status: z.enum(SERVICE_ORDER_FSM_STATES).optional(),
  clientId: z.string().uuid().optional(),
  costCenterId: z.string().uuid().optional(),
  search: z.string().max(256).optional(),
  page: z.coerce.number().int().min(1).optional(),
  pageSize: z.coerce.number().int().min(1).max(100).optional(),
  sortBy: z.enum(['orderNumber', 'createdAt']).optional(),
  sortDir: z.enum(['asc', 'desc']).optional(),
});

export type ServiceOrderBoardFilter = z.infer<typeof ServiceOrderBoardFilterSchema>;

// ---------------------------------------------------------------------------
// Execution contracts (SL-38 — locx-rental-bm Slice 5)
// ---------------------------------------------------------------------------

/**
 * ExecutionCreateSchema — POST body for creating an execution (remito) record.
 * Allowed for both alquiler and venta service types (execution capability ON for both).
 *
 * executedAt: ISO timestamp of when the remito/delivery occurred.
 * remitoNumber: optional external remito document number.
 * notes: optional freeform notes.
 *
 * executedBy is NOT accepted from the body — it is set from TenantContext.userId (SL-38.4).
 *
 * Spec: SL-38.5
 */
export const ExecutionCreateSchema = z.object({
  executedAt: z.string().datetime({ message: 'executedAt must be a valid ISO datetime string' }),
  remitoNumber: z.string().max(256, 'remitoNumber must be at most 256 characters').optional().nullable(),
  notes: z.string().optional().nullable(),
});

export type ExecutionCreate = z.infer<typeof ExecutionCreateSchema>;

/**
 * ExecutionResponseSchema — response shape for a service_order_execution row.
 * Conforms to the SL-6 invariant (typed, no raw EAV).
 *
 * Spec: SL-38.5
 */
export const ExecutionResponseSchema = z.object({
  id: z.string().uuid(),
  companyId: z.string().uuid(),
  serviceOrderId: z.string().uuid(),
  executedAt: z.string().datetime().or(z.date()),
  executedBy: z.string().uuid(),
  remitoNumber: z.string().nullable(),
  notes: z.string().nullable(),
  createdAt: z.string().datetime().or(z.date()),
});

export type ExecutionResponse = z.infer<typeof ExecutionResponseSchema>;

// ---------------------------------------------------------------------------
// Return contracts (SL-39 — locx-rental-bm Slice 5)
// ---------------------------------------------------------------------------

/**
 * ReturnCreateSchema — POST body for creating a return (devolución) record.
 * Allowed ONLY for alquiler service types (return capability OFF for venta/null).
 *
 * returnedAt: ISO timestamp of when the return occurred.
 * hasDisconformity: flag indicating equipment issue on return.
 * disconformityNotes: required when hasDisconformity=true (SL-39.5).
 *
 * returnedBy is NOT accepted from the body — it is set from TenantContext.userId (SL-39.4).
 *
 * Spec: SL-39.5, SL-39.6
 */
export const ReturnCreateSchema = z
  .object({
    returnedAt: z.string().datetime({ message: 'returnedAt must be a valid ISO datetime string' }),
    hasDisconformity: z.boolean({ required_error: 'hasDisconformity is required' }),
    disconformityNotes: z
      .string()
      .min(1, 'disconformityNotes must not be empty when provided')
      .optional()
      .nullable(),
  })
  .superRefine((data, ctx) => {
    // SL-39.5: disconformityNotes required when hasDisconformity=true
    if (data.hasDisconformity && !data.disconformityNotes) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['disconformityNotes'],
        message: 'disconformityNotes is required when hasDisconformity is true',
      });
    }
  });

export type ReturnCreate = z.infer<typeof ReturnCreateSchema>;

/**
 * ReturnResponseSchema — response shape for a service_order_return row.
 * Conforms to the SL-6 invariant.
 *
 * Spec: SL-39.6
 */
export const ReturnResponseSchema = z.object({
  id: z.string().uuid(),
  companyId: z.string().uuid(),
  serviceOrderId: z.string().uuid(),
  returnedAt: z.string().datetime().or(z.date()),
  returnedBy: z.string().uuid(),
  hasDisconformity: z.boolean(),
  disconformityNotes: z.string().nullable(),
  createdAt: z.string().datetime().or(z.date()),
});

export type ReturnResponse = z.infer<typeof ReturnResponseSchema>;

// ---------------------------------------------------------------------------
// service_order_passenger — external (non-employee) passengers carried on a
// service order (Transporte de Pasajeros, 2026-07-13). Free-text name/document
// only — these are the client's people, never rrhh.person/employment.
// ---------------------------------------------------------------------------

export const ServiceOrderPassengerInputSchema = z.object({
  fullName: z.string().min(1, 'fullName is required'),
  documentNumber: z.string().optional(),
});
export type ServiceOrderPassengerInput = z.infer<typeof ServiceOrderPassengerInputSchema>;

/** POST body — replaces the whole passenger list for the order in one call. */
export const ServiceOrderPassengersCreateSchema = z.object({
  passengers: z.array(ServiceOrderPassengerInputSchema).min(1, 'at least one passenger is required'),
});
export type ServiceOrderPassengersCreate = z.infer<typeof ServiceOrderPassengersCreateSchema>;

export const ServiceOrderPassengerResponseSchema = z.object({
  id: z.string().uuid(),
  companyId: z.string().uuid(),
  serviceOrderId: z.string().uuid(),
  fullName: z.string(),
  documentNumber: z.string().nullable(),
  position: z.number().int(),
  createdAt: z.string().datetime().or(z.date()),
});
export type ServiceOrderPassengerResponse = z.infer<typeof ServiceOrderPassengerResponseSchema>;
